/* =============================================================================
   VIEWS / ADMIN / SETTINGS-DISPLAYS — Sub-Tab "Displays" (KDS/Theke/Liefer-Cockpit)
   =============================================================================
   S66 Refactor: aus settings.js extrahiert (Zeilen 1100-1380).

   Pro Display lässt sich einstellen:
     - Ampel-Schwellzeiten pro Bestell-Phase (pending/preparing/ready
       für KDS+Theke, assigned/out_for_delivery für Lieferung — S62 HF7)
     - Vorlaufzeit für Vorbestellungen (preorderLeadMin)
     - Reihenfolge + Sichtbarkeit der Kachel-Felder
     - Live-Vorschau (renderDisplayPreview)

   Action-Handler:
     - displaySetThreshold(displayId, phase, value)
     - displaySetPreorderLead(displayId, value)
     - displayToggleField(displayId, fieldId, visible)
     - displayMoveField(displayId, idx, delta)

   Memory `feedback_gg_non_destructive_render`: refreshDisplayCard() rendert
   nur die einzelne Card neu (statt voll-Re-Render des Settings-Tabs), erhält
   Input-Focus + Selection + Scroll-Position.
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { DISPLAY_FIELDS, getDisplayConfig, setDisplayConfig, isDisplayFieldVisible } from '../../core/display-config.js';
// S126 Befund 6 — „Dieses Gerät"-Karte (Operator-Layer: Name + Idle-Timeout).
import { getDevice, registerDevice } from '../../core/operator-session.js';

// -----------------------------------------------------------------------------
// Section: Displays — KDS + Theken-Display individuell konfigurieren (P10)
// -----------------------------------------------------------------------------
export function renderDisplaysSection() {
  return `
    ${renderDeviceSettingsCard()}
    <p class="hint" style="margin-bottom: var(--space-4);">
      Pro Display lässt sich einstellen, wann die Ampel (grün → gelb → rot)
      umspringt und welche Info-Felder in welcher Reihenfolge auf jeder Kachel
      erscheinen sollen. Die Schwellzeiten gelten pro Bestell-Phase:
      Wartezeit unter <em>halber Schwelle</em> → grün, unter Schwelle → gelb,
      darüber → rot mit Puls-Animation.
    </p>
    <div class="settings-grid display-config-grid">
      ${renderDisplayCard('kds',      '🍳 Küchen-Display', 'KDS')}
      ${renderDisplayCard('bar',      '🥤 Theken-Display', 'Theke')}
      ${renderDisplayCard('delivery', '🛵 Liefer-Cockpit', 'Lieferung')}
    </div>
  `;
}

export function renderDisplayCard(displayId, title, shortLabel) {
  // Wrapper-Element mit data-display-id, damit der Action-Handler nach einem
  // Setting-Update gezielt NUR diese Card neu rendern kann — kein voll-Re-
  // Render des Settings-Tabs (würde Focus, Scroll und Cursor-Position killen,
  // genau wie der Session-13.1-Bug bei switchTab/Token-Refresh).
  return `<div data-display-id="${displayId}">${renderDisplayCardInner(displayId, title, shortLabel)}</div>`;
}

export function renderDisplayCardInner(displayId, title, shortLabel) {
  const cfg = getDisplayConfig(displayId);
  const t = cfg.thresholds;

  const fieldsHtml = cfg.fields.map((field, idx) => {
    const meta = DISPLAY_FIELDS.find(f => f.id === field.id) || { label: field.id, desc: '' };
    const isFirst = idx === 0;
    const isLast  = idx === cfg.fields.length - 1;
    return `
      <div class="display-field-row ${field.visible ? '' : 'is-hidden'}">
        <div class="display-field-order">
          <button class="display-field-arrow" onclick="displayMoveField('${displayId}', ${idx}, -1)"
                  ${isFirst ? 'disabled' : ''} title="Nach oben">▲</button>
          <button class="display-field-arrow" onclick="displayMoveField('${displayId}', ${idx}, 1)"
                  ${isLast ? 'disabled' : ''} title="Nach unten">▼</button>
        </div>
        <div class="display-field-info">
          <div class="display-field-label">${htmlEscape(meta.label)}</div>
          <div class="display-field-desc">${htmlEscape(meta.desc)}</div>
        </div>
        <label class="toggle">
          <input type="checkbox" ${field.visible ? 'checked' : ''}
                 onchange="displayToggleField('${displayId}', '${field.id}', this.checked)">
          <span class="toggle-slider"></span>
        </label>
      </div>
    `;
  }).join('');

  // S62 HF6+HF7 — Threshold-Labels pro Display-Typ. KDS+Theke nutzen den
  // klassischen pending→preparing→ready-Lifecycle, Lieferung hat nach HF7
  // den vereinfachten 2-Step-Workflow (pending hat keine Schwelle, weil
  // bei „Neu" die Verantwortung beim Disponent liegt).
  const thresholdLabels = (displayId === 'delivery')
    ? [
        { key: 'assigned',         label: '🧑‍🍳 Zugewiesen',          desc: 'Fahrer benannt, wartet auf Küche/Tour' },
        { key: 'out_for_delivery', label: '🛵 In Auslieferung',     desc: 'Fahrer auf Tour zum Kunden' }
      ]
    : [
        { key: 'pending',   label: '📥 Neu / Pending',    desc: '' },
        { key: 'preparing', label: '🔥 In Zubereitung',   desc: '' },
        { key: 'ready',     label: '✅ Bereit / Wartet',  desc: '' }
      ];

  return `
    <div class="card display-config-card">
      <h2>${title}</h2>

      <h3 style="margin-top: var(--space-3); font-size: 1rem;">Ampel-Schwellen pro Phase</h3>
      <p class="hint" style="margin-bottom: var(--space-3);">
        Wann darf eine Bestellung in dieser Phase verbringen, bevor die Ampel
        auf gelb (halbe Zeit) bzw. rot (volle Zeit) springt?
      </p>
      <div class="display-thresholds">
        ${thresholdLabels.map(({key, label, desc}) => {
          const val = t[key] != null ? t[key] : 5;
          return `
            <div class="display-threshold-row">
              <label>
                <span>${label}${desc ? ` <span class="hint" style="display:block;font-size:.75rem;color:var(--ink-muted);font-weight:normal;">${htmlEscape(desc)}</span>` : ''}</span>
                <input type="number" min="1" max="120" step="1" value="${val}"
                       onchange="displaySetThreshold('${displayId}', '${key}', this.value)">
                <span class="display-threshold-unit">Min.</span>
              </label>
              <div class="ampel-preview">
                <span class="ampel-dot wait-green"  title="< ${Math.floor(val / 2)} Min"></span>
                <span class="ampel-dot wait-yellow" title="${Math.floor(val / 2)}–${val} Min"></span>
                <span class="ampel-dot wait-red"    title="> ${val} Min"></span>
              </div>
            </div>
          `;
        }).join('')}
      </div>

      <h3 style="margin-top: var(--space-5); font-size: 1rem;">📅 Vorlaufzeit für Vorbestellungen</h3>
      <p class="hint" style="margin-bottom: var(--space-3);">
        Bei Reservierungs-Vorbestellungen pausiert der Wartezeit-Timer und zeigt
        „Service in X min". Sobald die Vorlaufzeit erreicht ist (X Min. vor
        Service-Zeit), startet der Timer normal hochlaufend mit Ampel — als wäre
        es eine frische Walk-In-Bestellung. Klickt der Bediener schon vorher auf
        „Annehmen"/„Zubereiten", startet der Timer ebenfalls sofort.
      </p>
      <div class="display-thresholds">
        <div class="display-threshold-row">
          <label>
            <span>⏰ Vorlauf vor Service-Zeit</span>
            <input type="number" min="0" max="240" step="5" value="${cfg.preorderLeadMin}"
                   onchange="displaySetPreorderLead('${displayId}', this.value)">
            <span class="display-threshold-unit">Min.</span>
          </label>
          <div class="ampel-preview" style="display: flex; align-items: center; gap: 6px; color: var(--ink-soft); font-size: 0.85rem;">
            <span class="ampel-dot wait-paused" style="background: #8b5cf6;"></span>
            <span>wartet</span>
            <span>→</span>
            <span class="ampel-dot wait-green"></span>
            <span>läuft</span>
          </div>
        </div>
      </div>

      <h3 style="margin-top: var(--space-5); font-size: 1rem;">Kachel-Felder (Reihenfolge &amp; Sichtbarkeit)</h3>
      <p class="hint" style="margin-bottom: var(--space-3);">
        Sortiere mit den Pfeilen, schalte mit dem Toggle die Sichtbarkeit. Items-Liste
        und Status-Button bleiben strukturell fix.
      </p>
      <div class="display-field-list">${fieldsHtml}</div>

      <h3 style="margin-top: var(--space-5); font-size: 1rem;">Live-Vorschau</h3>
      <div class="display-preview ${displayId === 'kds' ? 'preview-kds' : 'preview-bar'}">
        ${renderDisplayPreview(displayId, shortLabel)}
      </div>
    </div>
  `;
}

/** Mini-Tile-Preview, identisch zur echten Render-Logik, aber mit Beispiel-Order. */
export function renderDisplayPreview(displayId, shortLabel) {
  const cfg = getDisplayConfig(displayId);
  // Beispiel-Order (5 Min Wartezeit → Ampel-Farbe abhängig von pending-Schwelle)
  const exampleMins = 5;
  const order = { status: 'pending', orderNumber: '#00042', customerName: 'Max Mustermann', pickupTime: '19:30', notes: 'Mit Pommes extra' };
  const t = cfg.thresholds.pending;
  let waitCls = 'wait-green';
  if (exampleMins >= t) waitCls = 'wait-red';
  else if (exampleMins >= t / 2) waitCls = 'wait-yellow';

  const fieldHtml = {
    orderNumber: `<div class="${displayId === 'kds' ? 'kds' : 'bar'}-tile-num">${order.orderNumber}</div>`,
    waitTime:    `<div class="${displayId === 'kds' ? 'kds' : 'bar'}-tile-wait">${exampleMins} min</div>`,
    customer:    `<div class="${displayId === 'kds' ? 'kds' : 'bar'}-tile-line"><strong>${order.customerName}</strong></div>`,
    pickup:      `<div class="${displayId === 'kds' ? 'kds' : 'bar'}-tile-line">Abholung: <strong>${order.pickupTime}</strong></div>`,
    status:      `<div class="${displayId === 'kds' ? 'kds' : 'bar'}-tile-status">Neu</div>`,
    note:        `<div class="${displayId === 'kds' ? 'kds-note' : 'bar-note'}">📝 ${order.notes}</div>`
  };
  const ordered = cfg.fields
    .filter(f => f.visible && fieldHtml[f.id])
    .map(f => fieldHtml[f.id])
    .join('');

  // S196 (Jonas) — Positions-Notiz in der Vorschau, gated über das „Notiz"-Feld,
  // damit der Toggle-Effekt sichtbar ist (getrennt von der Order-„Bemerkung").
  const noteCls = displayId === 'kds' ? 'kds-item-note' : 'bar-item-note';
  const itemNoteHtml = isDisplayFieldVisible(displayId, 'itemNote')
    ? `<div class="${noteCls}">📝 Beispiel-Notiz zur Position</div>` : '';

  return `
    <div class="${displayId === 'kds' ? 'kds-tile' : 'bar-tile'} ${waitCls}" style="cursor: default; pointer-events: none;">
      <div class="${displayId === 'kds' ? 'kds-tile-fields' : 'bar-tile-fields'}">${ordered}</div>
      <ul class="${displayId === 'kds' ? 'kds-items' : 'bar-items'}">
        <li class="${displayId === 'kds' ? 'kds-item' : 'bar-item'}">
          <span class="${displayId === 'kds' ? 'kds-item-qty' : 'bar-item-qty'}">2×</span>
          <span class="${displayId === 'kds' ? 'kds-item-name' : 'bar-item-name'}">Beispiel-${shortLabel}-Position</span>
          ${itemNoteHtml}
        </li>
      </ul>
    </div>
  `;
}

// — Action-Handler für Display-Settings ———————————————————————
//
// WICHTIG: Hier dürfen wir NICHT renderSettings() aufrufen — das würde den
// ganzen Settings-Tab neu zeichnen und Focus/Scroll/Cursor-Position killen
// (gleiche Bug-Klasse wie der Session-13.1-Token-Refresh-Tab-Reset).
// Stattdessen rendern wir gezielt nur die betroffene Display-Card neu, via
// das `data-display-id`-Attribut.

/**
 * Rendert NUR die Display-Card des angegebenen Displays neu. Erhält Focus
 * (auf den zuletzt fokussierten Input innerhalb der Card) und Scroll-Position.
 */
function refreshDisplayCard(displayId) {
  const wrapper = document.querySelector(`[data-display-id="${displayId}"]`);
  if (!wrapper) return;

  // Focus-State sichern: welcher Input war fokussiert (per Position innerhalb
  // der Card), wie viel Selection?
  const active = document.activeElement;
  let focusInfo = null;
  if (active && wrapper.contains(active) && active.tagName === 'INPUT') {
    const inputs = Array.from(wrapper.querySelectorAll('input'));
    const idx = inputs.indexOf(active);
    if (idx !== -1) {
      focusInfo = {
        idx,
        selStart: active.selectionStart,
        selEnd:   active.selectionEnd
      };
    }
  }

  // Scroll-Position des Settings-Bodys sichern
  const settingsBody = wrapper.closest('.settings-body');
  const scrollY = settingsBody ? window.scrollY : 0;

  // S62 HF6 — Title + shortLabel hängen am displayId — wir wissen das hier statisch.
  // S66 Refactor: Liefer-Cockpit hinzugefügt (war im Original-Code in settings.js
  // vergessen, fiel nicht auf weil Liefer-Cockpit-Card sich selten refreshd).
  const metaMap = {
    'kds':      { title: '🍳 Küchen-Display', shortLabel: 'KDS' },
    'bar':      { title: '🥤 Theken-Display', shortLabel: 'Theke' },
    'delivery': { title: '🛵 Liefer-Cockpit', shortLabel: 'Lieferung' }
  };
  const meta = metaMap[displayId] || { title: displayId, shortLabel: displayId };
  wrapper.innerHTML = renderDisplayCardInner(displayId, meta.title, meta.shortLabel);

  // Focus wiederherstellen
  if (focusInfo) {
    const inputs = Array.from(wrapper.querySelectorAll('input'));
    const target = inputs[focusInfo.idx];
    if (target) {
      target.focus();
      try {
        if (target.type === 'number' || target.type === 'text') {
          target.setSelectionRange(focusInfo.selStart, focusInfo.selEnd);
        }
      } catch (_) { /* manche Input-Typen erlauben setSelectionRange nicht */ }
    }
  }
  if (settingsBody) window.scrollTo({ top: scrollY, behavior: 'instant' });
}

export async function displaySetThreshold(displayId, phase, value) {
  // S62 HF6 — Cap auf 120 Min angehoben (war 60). Liefer-Touren können
  // durchaus 60-90 Min dauern, da wäre 60 als Max-Schwelle zu eng.
  const v = Math.max(1, Math.min(120, parseInt(value, 10) || 1));
  const cur = getDisplayConfig(displayId);
  const next = { ...cur, thresholds: { ...cur.thresholds, [phase]: v } };
  await setDisplayConfig(displayId, next);
  refreshDisplayCard(displayId);
}

/**
 * Phase 18 (Session 22) — Vorlaufzeit für Vorbestellungen pro Display setzen.
 * Range 0–240 Min (0 = Timer startet exakt zur pickup_time, kein Vorlauf).
 */
export async function displaySetPreorderLead(displayId, value) {
  const v = Math.max(0, Math.min(240, parseInt(value, 10) || 0));
  const cur = getDisplayConfig(displayId);
  const next = { ...cur, preorderLeadMin: v };
  await setDisplayConfig(displayId, next);
  refreshDisplayCard(displayId);
}

export async function displayToggleField(displayId, fieldId, visible) {
  const cur = getDisplayConfig(displayId);
  const next = {
    ...cur,
    fields: cur.fields.map(f => f.id === fieldId ? { ...f, visible: !!visible } : f)
  };
  await setDisplayConfig(displayId, next);
  refreshDisplayCard(displayId);
}

export async function displayMoveField(displayId, idx, delta) {
  const cur = getDisplayConfig(displayId);
  const fields = cur.fields.slice();
  const newIdx = idx + delta;
  if (newIdx < 0 || newIdx >= fields.length) return;
  const [moved] = fields.splice(idx, 1);
  fields.splice(newIdx, 0, moved);
  await setDisplayConfig(displayId, { ...cur, fields });
  refreshDisplayCard(displayId);
}

// -----------------------------------------------------------------------------
// S126 Befund 6 — „Dieses Gerät"-Karte (Operator-Layer): Geräte-Name.
// Owner/Admin-only. Speichert via register_device (Update-Pfad, created:false).
// S222 Cut 2 — Gerät = nur noch Identifikation: der Idle-Timeout-Select ist raus,
// die Sperre bei Inaktivität wird pro Rolle im Team-Tab gesteuert.
// -----------------------------------------------------------------------------
function _deviceRole() {
  try {
    const u = window.App && window.App.auth && typeof window.App.auth.getCurrentUserSync === 'function'
      ? window.App.auth.getCurrentUserSync() : null;
    return (u && u.role) || null;
  } catch (_) { return null; }
}

export function renderDeviceSettingsCard() {
  const role = _deviceRole();
  if (role !== 'owner' && role !== 'admin') return '';
  return `<div class="card" id="settings-device-card" style="margin-bottom: var(--space-4);">${_deviceCardInner()}</div>`;
}

function _deviceCardInner() {
  const dev = getDevice();
  if (!dev) {
    return `
      <h2>Dieses Gerät</h2>
      <p class="hint" style="margin-bottom:12px;">Dieses Gerät ist noch nicht registriert.</p>
      <button class="btn btn-primary btn-sm" onclick="operatorReopenOnboarding()" style="margin:0;">Gerät benennen</button>
    `;
  }
  return `
    <h2>Dieses Gerät</h2>
    <p class="hint" style="margin-bottom:12px;">
      Nur der Geräte-Name (gilt für dieses Gerät/diesen Browser). Die automatische
      Operator-Sperre bei Inaktivität wird pro Rolle im Team-Tab gesteuert
      (Einstellungen → Team).
    </p>
    <div class="field-row" style="gap:10px; flex-wrap:wrap; align-items:flex-end;">
      <div class="field" style="flex:1; min-width:160px;">
        <label style="font-size:.8rem;">Geräte-Name</label>
        <input type="text" id="settings-device-label" maxlength="40" value="${htmlEscape(dev.label || '')}" placeholder="Theke">
      </div>
      <div class="field" style="flex:0 0 auto;">
        <button class="btn btn-primary btn-sm" onclick="saveDeviceSettings()" style="margin:0;">Speichern</button>
      </div>
    </div>
  `;
}

export async function saveDeviceSettings() {
  const labelEl = document.getElementById('settings-device-label');
  const label = labelEl ? (labelEl.value || '').trim() : '';
  if (!label) { showToast('Bitte einen Geräte-Namen angeben.', 'error'); return; }
  // S222 Cut 2 — Timeout ist rollenbasiert (Team-Tab); den bestehenden (toten)
  // Spalten-Wert unverändert durchreichen, statt ihn hier zu überschreiben.
  const dev = getDevice();
  const res = await registerDevice(label, Number(dev && dev.idle_timeout_minutes) || 0);
  if (!res || !res.ok) {
    const msg = res && res.error && (res.error.message || String(res.error));
    showToast('Speichern fehlgeschlagen: ' + (msg || 'Unbekannter Fehler'), 'error');
    return;
  }
  showToast('Geräte-Einstellungen gespeichert.', 'success');
  const card = document.getElementById('settings-device-card');
  if (card) card.innerHTML = _deviceCardInner();
}

// Side-Effect: window.* für Inline-Handler (onclick/onchange in den
// generierten HTML-Strings via dom-bindings-Parser → window-Auflösung).
if (typeof window !== 'undefined') {
  Object.assign(window, {
    displaySetThreshold,
    displaySetPreorderLead,
    displayToggleField,
    displayMoveField,
    saveDeviceSettings
  });
}
